import { Amplify } from 'aws-amplify'
import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signUp,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth'
import { logger } from 'services/logger'
import { LOGIN_CALLBACK_URL } from '../../config/config'
import { LOGGED_OUT_PARAM } from '../LoginUtills'
import {
  COGNITO_NOT_AUTHORIZED_ERROR_NAME,
  type CognitoUser,
} from './cognito.types'
import { type KeysValue } from '../KeysProvider'
// –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
// Amplify / Cognito configuration
// Note: All values are placeholders – update via env / runtime keys.
// https://www.notion.so/mixtiles/Migrating-Auth0-to-AWS-cognito-1fa09f38c373802bb038e3d6e72276f7
// –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––

const COGNITO_STORAGE_PREFIX = 'CognitoIdentityServiceProvider.'

function purgeStaleCognitoTokens(
  userPoolId: string,
  clientId: string,
  region: string
) {
  if (typeof window === 'undefined' || !clientId || !userPoolId || !region) {
    return
  }

  const expectedIssuer = `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`
  const toRemove: string[] = []

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key?.startsWith(COGNITO_STORAGE_PREFIX)) continue

    const keyClientId = key.slice(COGNITO_STORAGE_PREFIX.length).split('.')[0]

    if (keyClientId !== clientId) {
      // orphan from a previous clientId — Amplify will never read it
      toRemove.push(key)
      continue
    }

    if (key.endsWith('.idToken') || key.endsWith('.accessToken')) {
      try {
        const token = localStorage.getItem(key)
        const payload = token ? JSON.parse(atob(token.split('.')[1])) : null
        if (payload?.iss && payload.iss !== expectedIssuer) {
          toRemove.push(key)
        }
      } catch {
        toRemove.push(key)
      }
    }
  }

  toRemove.forEach((k) => localStorage.removeItem(k))
  if (toRemove.length > 0) {
    logger.info('Purged stale Cognito tokens from localStorage', {
      count: toRemove.length,
    })
  }
}

export const initCognito = (keys: KeysValue) => {
  const USER_POOL_ID = keys.cognitoUserPoolId || ''
  const USER_POOL_CLIENT_ID = keys.cognitoWebClientId || ''
  const COGNITO_DOMAIN = keys.cognitoDomain || ''
  const COGNITO_REGION = keys.cognitoRegion || ''

  if (!USER_POOL_ID || !USER_POOL_CLIENT_ID || !COGNITO_DOMAIN) {
    logger.error('Missing Cognito configuration', {
      USER_POOL_ID,
      USER_POOL_CLIENT_ID,
      COGNITO_DOMAIN,
    })
  }

  purgeStaleCognitoTokens(USER_POOL_ID, USER_POOL_CLIENT_ID, COGNITO_REGION)

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: USER_POOL_ID,
        userPoolClientId: USER_POOL_CLIENT_ID,
        loginWith: {
          oauth: {
            domain: COGNITO_DOMAIN,
            scopes: ['email', 'openid', 'profile'],
            redirectSignIn: [
              typeof window !== 'undefined'
                ? `${window.location.origin}${LOGIN_CALLBACK_URL}`
                : '',
            ],
            redirectSignOut: [
              typeof window !== 'undefined'
                ? `${window.location.origin}/?${LOGGED_OUT_PARAM}=1`
                : '',
            ],
            responseType: 'code',
          },
          email: true,
        },
      },
    },
  })
}

// –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
// Facade that mirrors the old AuthProvider helpers
// –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––

// Password required by Cognito signUp but never used (we use OTP custom auth)
const UNUSED_PASSWORD = 'NotUsed123!'

/**
 * Create user via client-side signUp (triggers preSignUp Lambda for auto-confirm).
 *
 * We always attempt signUp before signIn because:
 * 1. Cognito has no client-side "check if user exists" API (intentionally, to prevent enumeration)
 * 2. With "Prevent user existence errors" enabled, signIn doesn't reveal if user exists
 * 3. signUp is idempotent for our use case - UsernameExistsException is safely ignored
 *
 */
async function createUser(email: string): Promise<void> {
  try {
    await signUp({
      username: email,
      password: UNUSED_PASSWORD,
      options: {
        userAttributes: { email },
      },
    })
    logger.info('User created successfully', { email })
  } catch (e) {
    // UsernameExistsException means user already exists - that's fine, proceed to signIn
    if (e instanceof Error && e.name === 'UsernameExistsException') {
      logger.info('User already exists, proceeding to signIn', { email })
      return
    }
    throw e
  }
}

export async function initiateEmailSignIn(email: string) {
  const doSignIn = async () => {
    return signIn({
      username: email,
      options: {
        authFlowType: 'CUSTOM_WITHOUT_SRP',
        clientMetadata: { email },
      },
    })
  }

  // Always try to create user first (will be ignored if exists)
  await createUser(email)

  // Now sign in
  const { nextStep: signInNextStep } = await doSignIn()
  logger.info('SignIn response', {
    email,
    signInStep: signInNextStep.signInStep,
  })

  return signInNextStep
}

export const INVALID_CODE_ERROR = 'InvalidCodeException'
export const OTP_SESSION_EXPIRED_ERROR = 'OtpSessionExpiredException'

class InvalidCodeException extends Error {
  constructor() {
    super('Invalid verification code')
    this.name = INVALID_CODE_ERROR
  }
}

class OtpSessionExpiredException extends Error {
  constructor() {
    super('Invalid verification code')
    this.name = OTP_SESSION_EXPIRED_ERROR
  }
}

export async function confirmEmailCode(user: { email: string }, code: string) {
  try {
    const result = await confirmSignIn({
      challengeResponse: code,
      options: {
        clientMetadata: { email: user.email },
      },
    })

    // Amplify doesn't throw on wrong OTP while retries remain — it returns
    // isSignedIn=false and re-issues CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE.
    if (!result.isSignedIn) {
      throw new InvalidCodeException()
    }

    return result
  } catch (err) {
    // Max attempts reached — defineAuthChallenge sets failAuthentication=true
    // and Amplify throws NotAuthorizedException. The OTP session is now dead;
    // the user must request a new code.
    if (
      err instanceof Error &&
      err.name === COGNITO_NOT_AUTHORIZED_ERROR_NAME
    ) {
      throw new OtpSessionExpiredException()
    }
    throw err
  }
}

export const getAccessToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.accessToken?.toString() || null
  } catch (err) {
    logger.info('No session for getAccessToken', { err })
    return null
  }
}
// Rejects when a token refresh failed; resolves null only when Cognito holds no tokens
export const fetchIdToken = async (): Promise<string | null> => {
  const session = await fetchAuthSession()
  return session.tokens?.idToken?.toString() || null
}

export const getIdToken = async (): Promise<string | null> => {
  try {
    return await fetchIdToken()
  } catch (err) {
    logger.info('No session for getIdToken', { err })
    return null
  }
}

export const getFreshIdToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true })
    return session.tokens?.idToken?.toString() || null
  } catch (err) {
    logger.info('No session for getFreshIdToken', { err })
    return null
  }
}

async function getUserAttributesFromToken() {
  const idToken = await getIdToken()
  if (idToken) {
    return JSON.parse(atob(idToken.split('.')[1]))
  } else {
    return {}
  }
}

export const signInWithGoogle = () => signInWithRedirect({ provider: 'Google' })
export const signInWithApple = () => signInWithRedirect({ provider: 'Apple' })

export const getUser = async (): Promise<CognitoUser> => {
  const user = await getCurrentUser()
  const attributes = await getUserAttributesFromToken()
  return { ...user, ...attributes }
}

export const signOut = () => amplifySignOut()
